import moment from "moment";
import {
  Field,
  Step,
  Option,
  StepEndpointSecurities,
  StepFiveSecurities,
  StepSixSecurities,
  BENEFICIARIES_LIMIT,
  SecuritiesFormikValues,
  BeneficiarySetupFields,
  BeneficiaryFields,
  SerializableFile,
  DocumentType,
} from "../../types";
import styles from "../../styles/securities.module.scss";
import InfoIcon from "../../assets/images/icons/ui/tooltip.svg";
import QuestionIcon from "../../assets/images/icons/ui/ui-icon-tooltip-question.svg";
import AddIcon from "../../assets/images/icons/ui/ui-plus-circle-icon.svg";
import {
  onSaveCurrentHolder,
  onUpdateAccountBeneficiaries,
  onUpdateCompletedStep,
  onUpdateLastCompletedSubStep,
  onUpdateReachedStep,
  onUpdateSubStep,
  addSendedStep,
  onResetValidationErrorCounts,
} from "../../reducers/securitiesReducer";
import { FormikValues } from "formik";
import {
  descriptions,
  labels,
  placeholders,
  preconfirmationLabels,
  subStepNames,
  texts,
  title,
} from "../../i18n/securities/stepFive";
import {
  accountObjectiveOptions,
  genderOptions,
  originOfFundsOptions,
  regularWithdrawalsFrequencyOptions,
  regularWithdrawalsReasonOptions,
  riskProfileOptions,
  taxIdTypesOptions,
  thirdPartyTransfersFrequencyOptions,
  thirdPartyTransfersReasonOptions,
  timeHorizonOptions,
  tradeOptionsObjectiveOptions,
  yesOrNoBoolOptions,
} from "../../i18n/securities/options";
import { AppDispatch, RootState } from "../../app/store";
import { validationsForLanguage } from "../../validations";
import {
  CreateDraftProps,
  sendIndividualDocument,
  sendStep,
} from "../../connectors/connectors";
import {
  formatAsCurrency,
  parseDateToSend,
  preconfirmationPhone,
  serializableFileAsFile,
  isEmptyFile,
  maxFileSize,
  validDocumentImageTypes,
  minFileSize,
  withPhonePlus,
} from "../../utils";
import { fileInfoText } from "../../i18n/inputs";
import { heicToJpeg } from "../../app/utils";
import { Alert } from "@allaria-tech/matter/Alert";
import {
  captureSecuritiesError,
  addSubStepBreadcrumb,
} from "../../sentry/utils";

const {
  formSectionTitle,
  label,
  labelDescription,
  marginless,
  multipleRadioContainer,
  documentImagesLabel,
} = styles;

const StepFive = ({
  language,
  countriesOptions,
  beneficiariesCount,
  saveFileToFirestore,
}: {
  language: "es" | "en";
  countriesOptions: Option[];
  beneficiariesCount: number;
  saveFileToFirestore?: (fileName: string, type: DocumentType) => void;
}): Step => {
  const validations = validationsForLanguage(language, {
    validateFileName: false,
  });

  const beneficiariesSetupFields = (
    beneficiaryIndex: number,
    beneficiariesCount: number
  ): Field[] => {
    return [
      {
        name: `beneficiary${beneficiaryIndex}.name`,
        label: labels[language].beneficiarySetup.name,
        type: "text",
        validation:
          beneficiaryIndex === 1
            ? validations.lettersRequired
            : validations.lettersOnly,
        initialValue: "",
        placeholder: placeholders[language].beneficiarySetup.name,
        columns: 2,
        conditionalBool: (values, _1, state) => {
          const beneficiaries = state?.securities.beneficiariesCount;
          const show = values.beneficiariesBool === "SI";
          return !!(show && beneficiaries && beneficiaries >= beneficiaryIndex);
        },
      },
      {
        // HACK: Can't access sibling fields beyond same parent, so, unlike
        // other beneficiary fields, percentages must be siblings of same parent
        name: `beneficiary${beneficiaryIndex}Percentage`,
        label: labels[language].beneficiarySetup.percentage,
        type: "text",
        validation: validations
          .percentagesAddUp(
            Array.from({ length: beneficiariesCount }, (_, i) => i + 1).map(
              (i) => `beneficiary${i}Percentage`
            )
          )
          .concat(validations.floatRequired),
        initialValue: "",
        placeholder: placeholders[language].beneficiarySetup.percentage,
        columns: 2,
        conditionalBool: (values, _1, state) => {
          const beneficiaries = state?.securities.beneficiariesCount;
          const show = values.beneficiariesBool === "SI";
          return !!(show && beneficiaries && beneficiaries >= beneficiaryIndex);
        },
      },
    ];
  };

  const fieldsForBeneficiary = (index: number): Field[] => {
    const showIfWithinChosenBeneficiariesTotal = (
      values: FormikValues,
      _dispatch?: AppDispatch,
      state?: RootState
    ) => {
      const beneficiaries = state?.securities.beneficiariesCount;
      const show = values.beneficiariesBool === "SI";
      return !!(show && beneficiaries && beneficiaries >= index);
    };

    return [
      {
        label: `${texts[language].beneficiary} ${index}`,
        initialValue: "",
        type: "label",
        name: `beneficiary${index}`,
        text: (
          <>
            <p className={`${formSectionTitle} ${marginless}`}>
              {`${texts[language].beneficiary} ${index}`}
            </p>
          </>
        ),
        conditionalBool: showIfWithinChosenBeneficiariesTotal,
      },
      {
        name: `beneficiary${index}.name`,
        label: labels[language].beneficiary.name,
        disabled: true,
        type: "text",
        initialValue: "",
        columns: 2,
        validation: validations.smallStringRequired,
        conditionalBool: showIfWithinChosenBeneficiariesTotal,
        preconfirmationLabel: `${labels[language].beneficiary.preconfirmationName} ${index}`,
      },
      {
        name: `beneficiary${index}Percentage`,
        label: labels[language].beneficiary.percentage,
        disabled: true,
        type: "text",
        initialValue: "",
        columns: 2,
        conditionalBool: showIfWithinChosenBeneficiariesTotal,
      },
      {
        name: `beneficiary${index}.gender`,
        label: labels[language].beneficiary.gender,
        type: "select",
        initialValue: "",
        placeholder: placeholders[language].beneficiary.gender,
        validation: validations.radioOrSingleSelectRequired(
          genderOptions[language]
        ),
        options: genderOptions[language],
        columns: 2,
        conditionalBool: showIfWithinChosenBeneficiariesTotal,
      },
      {
        name: `beneficiary${index}.birthDate`,
        label: labels[language].beneficiary.birthDate,
        type: "date",
        initialValue: "",
        placeholder: placeholders[language].beneficiary.birthDate,
        minDatePickerDate: moment("1900-01-01").toDate(),
        maxDatePickerDate: moment().subtract(1, "days").toDate(),
        validation: validations.previousDateRequired,
        columns: 2,
        conditionalBool: showIfWithinChosenBeneficiariesTotal,
      },
      {
        name: `beneficiary${index}.phoneAR`,
        type: "phone",
        country: "ar",
        label: labels[language].beneficiary.phone,
        validation: validations.internationalPhoneRequired,
        initialValue: "",
        formatInPreConfirmation: preconfirmationPhone,
        conditionalBool: (values, dispatch, state) =>
          showIfWithinChosenBeneficiariesTotal(values, dispatch, state) &&
          !state?.securities.USCitizen,
        preconfirmationLabel: preconfirmationLabels[language].beneficiaryPhone,
      },
      {
        name: `beneficiary${index}.phoneUS`,
        type: "phone",
        country: "us",
        label: labels[language].beneficiary.phone,
        validation: validations.internationalPhoneRequired,
        initialValue: "",
        formatInPreConfirmation: preconfirmationPhone,
        conditionalBool: (values, dispatch, state) =>
          showIfWithinChosenBeneficiariesTotal(values, dispatch, state) &&
          !!state?.securities.USCitizen,
        preconfirmationLabel: preconfirmationLabels[language].beneficiaryPhone,
      },
      {
        name: `beneficiary${index}.email`,
        label: labels[language].beneficiary.email,
        type: "text",
        initialValue: "",
        placeholder: placeholders[language].beneficiary.email,
        validation: validations.emailRequired,
        conditionalBool: showIfWithinChosenBeneficiariesTotal,
      },
      {
        name: `beneficiary${index}.taxIdType`,
        type: "select",
        label: labels[language].beneficiary.taxIdType,
        placeholder: placeholders[language].beneficiary.taxIdType,
        validation: validations.radioOrSingleSelectRequired(
          taxIdTypesOptions[language]
        ),
        options: taxIdTypesOptions[language],
        selectType: "single",
        conditionalBool: (values, dispatch, state?: RootState) =>
          showIfWithinChosenBeneficiariesTotal(values, dispatch, state) &&
          !state?.securities.USCitizen,
        initialValue: "",
      },
      {
        name: `beneficiary${index}.taxIdNumber`,
        type: "text",
        label: labels[language].beneficiary.taxIdNumber,
        validation: validations.smallStringRequired,
        initialValue: "",
        placeholder: placeholders[language].beneficiary.taxIdNumber,
        conditionalBool: (values, dispatch, state) =>
          showIfWithinChosenBeneficiariesTotal(values, dispatch, state) &&
          !state?.securities.USCitizen,
      },
      {
        name: `beneficiary${index}.socialSecurityNumber`,
        type: "text",
        label: labels[language].beneficiary.socialSecurityNumber,
        validation: validations.ssnRequired,
        initialValue: "",
        placeholder: placeholders[language].beneficiary.socialSecurityNumber,
        conditionalBool: (values, dispatch, state) =>
          showIfWithinChosenBeneficiariesTotal(values, dispatch, state) &&
          !!state?.securities.USCitizen,
      },
      {
        name: `beneficiary${index}.relationship`,
        label: labels[language].beneficiary.relationship,
        type: "text",
        initialValue: "",
        placeholder: placeholders[language].beneficiary.relationship,
        validation: validations.smallStringRequired,
        conditionalBool: showIfWithinChosenBeneficiariesTotal,
      },
      {
        name: `beneficiary${index}.country`,
        type: "select",
        label: labels[language].beneficiary.country,
        placeholder: placeholders[language].beneficiary.country,
        validation: validations.radioOrSingleSelectRequired(countriesOptions),
        options: countriesOptions,
        searchable: true,
        selectType: "single",
        initialValue: "",
        conditionalBool: showIfWithinChosenBeneficiariesTotal,
      },
      {
        name: `beneficiary${index}.fullAddress`,
        type: "address",
        label: labels[language].beneficiary.fullAddress,
        validation: validations.mediumStringRequired,
        initialValue: "",
        placeholder: placeholders[language].beneficiary.fullAddress,
        onChange: (values, setFieldValue) => {
          const zipCode = values.formattedAddress?.postalCode;
          const state = values.formattedAddress?.state;
          const city = values.formattedAddress?.city;
          const street = values.formattedAddress?.street;
          const number = values.formattedAddress?.streetNumber;

          setFieldValue(`beneficiary${index}.zipCode`, zipCode);
          setFieldValue(`beneficiary${index}.state`, state);
          setFieldValue(`beneficiary${index}.city`, city);
          setFieldValue(
            `beneficiary${index}.fullAddress`,
            [street, number].filter(Boolean).join(" "),
            true
          );
        },
        autoComplete: "off",
        conditionalBool: showIfWithinChosenBeneficiariesTotal,
      },
      {
        name: `beneficiary${index}.zipCode`,
        type: "text",
        label: labels[language].beneficiary.zipCode,
        validation: validations.zipCodeRequired,
        initialValue: "",
        placeholder: placeholders[language].beneficiary.zipCode,
        conditionalBool: showIfWithinChosenBeneficiariesTotal,
      },
      {
        name: `beneficiary${index}.floor`,
        type: "text",
        label: labels[language].beneficiary.floor,
        validation: validations.smallString,
        initialValue: "",
        placeholder: placeholders[language].beneficiary.floor,
        columns: 2,
        conditionalBool: showIfWithinChosenBeneficiariesTotal,
      },
      {
        name: `beneficiary${index}.apartment`,
        type: "text",
        label: labels[language].beneficiary.apartment,
        validation: validations.smallString,
        initialValue: "",
        placeholder: placeholders[language].beneficiary.apartment,
        columns: 2,
        conditionalBool: showIfWithinChosenBeneficiariesTotal,
      },
      {
        name: `beneficiary${index}.city`,
        type: "text",
        label: labels[language].beneficiary.city,
        validation: validations.smallStringRequired,
        initialValue: "",
        columns: 2,
        placeholder: placeholders[language].beneficiary.city,
        conditionalBool: showIfWithinChosenBeneficiariesTotal,
      },
      {
        name: `beneficiary${index}.state`,
        type: "text",
        label: labels[language].beneficiary.state,
        validation: validations.smallStringRequired,
        initialValue: "",
        placeholder: placeholders[language].beneficiary.state,
        conditionalBool: showIfWithinChosenBeneficiariesTotal,
        columns: 2,
      },
      {
        label: "",
        initialValue: "",
        type: "label",
        name: `beneficiary${index}.documentImagesLabel`,
        text: (
          <div className={documentImagesLabel}>
            <h2>{texts[language].documentImagesLabelTitle}</h2>
            <p>{texts[language].documentImagesLabelBody}</p>
          </div>
        ),
        conditionalBool: showIfWithinChosenBeneficiariesTotal,
      },
      {
        name: `beneficiary${index}.documentFront`,
        type: "file",
        label: labels[language].beneficiary.documentFront,
        validation: validations.fileRequired(
          validDocumentImageTypes,
          maxFileSize,
          minFileSize
        ),
        initialValue: "",
        infoText: fileInfoText(maxFileSize)[language],
        infoTextPosition: "bottom",
        maxSize: maxFileSize,
        validTypes: validDocumentImageTypes,
        conditionalBool: showIfWithinChosenBeneficiariesTotal,
      },
      {
        name: `beneficiary${index}.documentBack`,
        type: "file",
        label: labels[language].beneficiary.documentBack,
        conditionalValidation: (state) => {
          const usCitizen = state?.securities.USCitizen;
          return usCitizen
            ? validations.fileNotRequired(
                validDocumentImageTypes,
                maxFileSize,
                minFileSize
              )
            : validations.fileRequired(
                validDocumentImageTypes,
                maxFileSize,
                minFileSize
              );
        },
        initialValue: "",
        infoText: fileInfoText(maxFileSize)[language],
        infoTextPosition: "bottom",
        maxSize: maxFileSize,
        validTypes: validDocumentImageTypes,
        conditionalBool: showIfWithinChosenBeneficiariesTotal,
      },
    ];
  };

  return {
    step: 5,
    name: title[language],
    titleOfStep: title[language],
    label: title[language],
    subSteps: [
      {
        step: 1,
        name: subStepNames[language].planning,
        titleOfStep: subStepNames[language].planning,
        fields: [
          {
            label: "",
            initialValue: "",
            type: "label",
            name: "section1",
            text: (
              <>
                <p className={formSectionTitle}>{texts[language].section1}</p>
              </>
            ),
          },
          {
            name: "accountObjective",
            type: "select",
            label: labels[language].accountObjective,
            placeholder: placeholders[language].accountObjective,
            validation: validations.radioOrSingleSelectRequired(
              accountObjectiveOptions[language]
            ),
            options: accountObjectiveOptions[language],
            selectType: "single",
            initialValue: "",
            tooltip: {
              label: "¿Qué significa?",
              modal: "account-objective",
              icon: QuestionIcon,
            },
          },
          {
            name: "accountOpeningObjective",
            label: labels[language].accountOpeningObjective,
            type: "text",
            validation: validations.mediumStringRequired,
            initialValue: "",
            placeholder: placeholders[language].accountOpeningObjective,
          },
          {
            label: "",
            initialValue: "",
            type: "label",
            name: "originOfFundsLabel",
            text: (
              <div style={{ marginBottom: "-1rem" }}>
                <label className={label}>
                  {labels[language].originOfFunds}
                </label>
                <p className={labelDescription}>
                  {descriptions[language].originOfFunds}
                </p>
              </div>
            ),
          },
          {
            name: "originOfFunds",
            label: "",
            type: "radio",
            validation: validations.radioOrSingleSelectRequired(
              originOfFundsOptions[language]
            ),
            initialValue: "",
            options: originOfFundsOptions[language],
            className: multipleRadioContainer,
            formatInPreConfirmation: (currentValue) =>
              originOfFundsOptions[language].find(
                ({ label }) => label === currentValue
              )?.label || "",
            preconfirmationLabel: labels[language].originOfFunds,
          },
          {
            name: "otherOriginOfFunds",
            label: labels[language].otherOriginOfFunds,
            type: "text",
            validation: validations.smallStringRequired,
            initialValue: "",
            placeholder: placeholders[language].otherOriginOfFunds,
            conditionalBool: (values) =>
              values?.originOfFunds?.includes("OTHERS"),
          },
          {
            name: "fundingDate",
            label: labels[language].fundingDate,
            type: "text",
            validation: validations.numberRequired,
            initialValue: "",
            placeholder: placeholders[language].fundingDate,
          },
          {
            name: "initialTransferAmount",
            label: labels[language].initialTransferAmount,
            type: "money",
            validation: validations.smallStringRequired.concat(
              validations.smallerNumberRequiered({
                than: "netWorthUpperBound",
                message: texts[language].initialTransferAmountTooSmallMessage,
              })
            ),
            initialValue: "",
            placeholder: placeholders[language].initialTransferAmount,
            formatInPreConfirmation: (value) =>
              formatAsCurrency(
                value,
                language === "en" ? "en-US" : "es-AR",
                true
              ).toString(),
          },
          {
            label: "",
            initialValue: "",
            type: "label",
            name: "fundingSource",
            text: (
              <div>
                <label className={label}>
                  {labels[language].fundingSource}
                </label>
                <p className={labelDescription}>
                  {descriptions[language].fundingSource}
                </p>
              </div>
            ),
            preconfirmationLabel: preconfirmationLabels[language].fundingSource,
            formatInPreConfirmation(_, formValues) {
              return `${formValues?.fundingSourceBankName} - ${formValues?.fundingSourceAccountHolder}`;
            },
          },
          {
            name: "fundingSourceBankName",
            label: labels[language].fundingSourceBankName,
            type: "text",
            validation: validations.smallStringRequired,
            initialValue: "",
            placeholder: placeholders[language].fundingSourceBankName,
            columns: 2,
          },
          {
            name: "fundingSourceAccountHolder",
            label: labels[language].fundingSourceAccountHolder,
            type: "text",
            validation: validations.smallStringRequired,
            initialValue: "",
            placeholder: placeholders[language].fundingSourceAccountHolder,
            columns: 2,
          },
          {
            name: "timeHorizon",
            label: labels[language].timeHorizon,
            type: "select",
            placeholder: placeholders[language].timeHorizon,
            validation: validations.radioOrSingleSelectRequired(
              timeHorizonOptions[language]
            ),
            initialValue: "",
            options: timeHorizonOptions[language],
            tooltip: {
              label: "",
              content: texts[language].timeHorizonTooltip,
              icon: InfoIcon,
            },
            formatInPreConfirmation: (value: string) => {
              return value;
            },
          },
          {
            label: "",
            initialValue: "",
            type: "label",
            name: "section2",
            text: (
              <>
                <p className={formSectionTitle}>{texts[language].section2}</p>
              </>
            ),
          },
          {
            name: "riskProfile",
            type: "select",
            label: labels[language].riskProfile,
            placeholder: placeholders[language].riskProfile,
            validation: validations.radioOrSingleSelectRequired(
              riskProfileOptions[language]
            ),
            options: riskProfileOptions[language],
            selectType: "single",
            initialValue: "",
            tooltip: {
              label: "¿Qué significa?",
              modal: "risk-profile-securities",
              icon: QuestionIcon,
            },
          },
          {
            label: "",
            initialValue: "",
            type: "label",
            name: "section3",
            text: (
              <>
                <p className={formSectionTitle}>{texts[language].section3}</p>
              </>
            ),
          },
          {
            name: "regularWithdrawals.bool",
            label: labels[language].regularWithdrawals.bool,
            type: "radio",
            placeholder: placeholders[language].regularWithdrawals.bool,
            validation: validations.radioOrSingleSelectRequired(
              yesOrNoBoolOptions[language]
            ),
            initialValue: "SI",
            options: yesOrNoBoolOptions[language],
            formatInPreConfirmation: (value: string) => {
              return value;
            },
          },
          {
            name: "regularWithdrawals.reason",
            type: "select",
            label: labels[language].regularWithdrawals.reason,
            placeholder: placeholders[language].regularWithdrawals.reason,
            validation: validations.radioOrSingleSelectRequired(
              regularWithdrawalsReasonOptions[language]
            ),
            options: regularWithdrawalsReasonOptions[language],
            selectType: "single",
            initialValue: "",
            dontScrollTo: true,
            conditional: {
              name: "regularWithdrawals.bool",
              value: "SI",
            },
          },
          {
            name: "regularWithdrawals.otherReason",
            label: labels[language].regularWithdrawals.otherReason,
            type: "text",
            validation: validations.mediumStringRequired,
            initialValue: "",
            placeholder: placeholders[language].regularWithdrawals.otherReason,
            dontScrollTo: true,
            conditional: {
              name: "regularWithdrawals.reason",
              value: "OTHERS",
            },
          },
          {
            name: "regularWithdrawals.frequency",
            type: "select",
            label: labels[language].regularWithdrawals.frequency,
            placeholder: placeholders[language].regularWithdrawals.frequency,
            validation: validations.radioOrSingleSelectRequired(
              regularWithdrawalsFrequencyOptions[language]
            ),
            options: regularWithdrawalsFrequencyOptions[language],
            selectType: "single",
            initialValue: "",
            dontScrollTo: true,
            conditional: {
              name: "regularWithdrawals.bool",
              value: "SI",
            },
          },
          {
            name: "regularWithdrawals.otherFrequency",
            label: labels[language].regularWithdrawals.otherFrequency,
            type: "text",
            validation: validations.mediumStringRequired,
            initialValue: "",
            placeholder:
              placeholders[language].regularWithdrawals.otherFrequency,
            dontScrollTo: true,
            conditional: {
              name: "regularWithdrawals.frequency",
              value: "OTHER",
            },
          },
          {
            name: "thirdPartyTransfers.bool",
            label: labels[language].thirdPartyTransfers.bool,
            type: "radio",
            placeholder: placeholders[language].thirdPartyTransfers.bool,
            validation: validations.radioOrSingleSelectRequired(
              yesOrNoBoolOptions[language]
            ),
            initialValue: "SI",
            options: yesOrNoBoolOptions[language],
            formatInPreConfirmation: (value: string) => {
              return value;
            },
          },
          {
            name: "thirdPartyTransfers.reason",
            type: "select",
            label: labels[language].thirdPartyTransfers.reason,
            placeholder: placeholders[language].thirdPartyTransfers.reason,
            validation: validations.radioOrSingleSelectRequired(
              thirdPartyTransfersReasonOptions[language]
            ),
            options: thirdPartyTransfersReasonOptions[language],
            selectType: "single",
            initialValue: "",
            dontScrollTo: true,
            conditional: {
              name: "thirdPartyTransfers.bool",
              value: "SI",
            },
          },
          {
            name: "thirdPartyTransfers.otherReason",
            label: labels[language].thirdPartyTransfers.otherReason,
            type: "text",
            validation: validations.mediumStringRequired,
            initialValue: "",
            placeholder: placeholders[language].thirdPartyTransfers.otherReason,
            dontScrollTo: true,
            conditional: {
              name: "thirdPartyTransfers.reason",
              value: "OTHERS",
            },
          },
          {
            name: "thirdPartyTransfers.frequency",
            type: "select",
            label: labels[language].thirdPartyTransfers.frequency,
            placeholder: placeholders[language].thirdPartyTransfers.frequency,
            validation: validations.radioOrSingleSelectRequired(
              thirdPartyTransfersFrequencyOptions[language]
            ),
            options: thirdPartyTransfersFrequencyOptions[language],
            selectType: "single",
            initialValue: "",
            dontScrollTo: true,
            conditional: {
              name: "thirdPartyTransfers.bool",
              value: "SI",
            },
          },
          {
            name: "thirdPartyTransfers.otherFrequency",
            label: labels[language].thirdPartyTransfers.otherFrequency,
            type: "text",
            validation: validations.mediumStringRequired,
            initialValue: "",
            placeholder:
              placeholders[language].thirdPartyTransfers.otherFrequency,
            dontScrollTo: true,
            conditional: {
              name: "thirdPartyTransfers.frequency",
              value: "OTHER",
            },
          },
          {
            label: "",
            initialValue: "",
            type: "label",
            name: "section4",
            text: (
              <>
                <p className={formSectionTitle}>{texts[language].section4}</p>
              </>
            ),
          },
          {
            name: "tradeOptions.bool",
            label: labels[language].tradeOptions.bool,
            type: "radio",
            placeholder: placeholders[language].tradeOptions.bool,
            validation: validations.radioOrSingleSelectRequired(
              yesOrNoBoolOptions[language]
            ),
            initialValue: "SI",
            options: yesOrNoBoolOptions[language],
            formatInPreConfirmation: (value: string) => {
              return value;
            },
          },
          {
            label: "",
            initialValue: "",
            type: "label",
            name: "tradeOptions.label",
            text: (
              <div style={{ marginBottom: "-1rem" }}>
                <label className={label}>
                  {labels[language].tradeOptions.label}
                </label>
                <p className={labelDescription}>
                  {descriptions[language].tradeOptions}
                </p>
              </div>
            ),
            conditional: {
              name: "tradeOptions.bool",
              value: "SI",
            },
          },
          {
            name: "tradeOptions.options",
            label: labels[language].tradeOptions.label,
            hideLabel: true,
            type: "multiple-checkbox",
            initialValue: "",
            options: tradeOptionsObjectiveOptions[language],
            validation: validations.anyCheckboxCheckedRequired,
            formatInPreConfirmation: (_, values) =>
              tradeOptionsObjectiveOptions[language]
                .filter(({ value }) =>
                  (values?.tradeOptions.options as string[])?.find(
                    (selected) => selected === value
                  )
                )
                .map((option) => option.label)
                .join(", "),
            conditional: {
              name: "tradeOptions.bool",
              value: "SI",
            },
          },
          {
            name: "tradeMargins.bool",
            label: labels[language].tradeMargins.bool,
            type: "radio",
            placeholder: placeholders[language].tradeMargins.bool,
            validation: validations.radioOrSingleSelectRequired(
              yesOrNoBoolOptions[language]
            ),
            initialValue: "SI",
            options: yesOrNoBoolOptions[language],
            formatInPreConfirmation: (value: string) => {
              return value;
            },
          },
          {
            label: "",
            initialValue: "",
            type: "label",
            name: "section5",
            text: (
              <>
                <p className={`${formSectionTitle} ${marginless}`}>
                  {texts[language].section5}
                </p>
                <div style={{ marginTop: "1rem" }}>
                  <Alert
                    variant="INFO"
                    title={texts[language].important}
                    children={texts[language].beneficiariesInfo}
                  />
                </div>
              </>
            ),
          },
          {
            name: "beneficiariesBool",
            label: labels[language].beneficiariesBool,
            type: "radio",
            placeholder: placeholders[language].beneficiariesBool,
            validation: validations.radioOrSingleSelectRequired(
              yesOrNoBoolOptions[language]
            ),
            initialValue: "SI",
            options: yesOrNoBoolOptions[language],
            formatInPreConfirmation: (value: string) => {
              return value;
            },
          },
          ...Array.from({ length: beneficiariesCount }, (_, i) => i).flatMap(
            (i) => beneficiariesSetupFields(i + 1, beneficiariesCount)
          ),
          {
            name: "addBeneficiaryButton",
            label: "",
            type: "button",
            initialValue: "",
            buttonProps: {
              text: texts[language].addBeneficiaryButton,
              type: "button",
              variant: "tertiary",
              startContent: <img src={AddIcon} className="mr-2" />,
              disabled: (_, state) => {
                const beneficiaries = state.securities.beneficiariesCount;

                return beneficiaries >= BENEFICIARIES_LIMIT;
              },
              onClick: (_, dispatch, state) => {
                const beneficiaries = state?.securities.beneficiariesCount;
                dispatch &&
                  beneficiaries !== undefined &&
                  beneficiaries < BENEFICIARIES_LIMIT &&
                  dispatch(
                    onUpdateAccountBeneficiaries({
                      count: beneficiaries + 1,
                      bool: true,
                    })
                  );
              },
            },
            conditional: {
              name: "beneficiariesBool",
              value: "SI",
            },
          },
        ],
        onContinue: async (values: FormikValues, dispatch, state) => {
          addSubStepBreadcrumb(5, 1, "enter");
          if (!state || !dispatch)
            throw new Error("Error: state or dispatch not found");
          const formValues =
            values as SecuritiesFormikValues["stepFiveFirstSubStep"];
          const { beneficiariesBool } = formValues;
          const beneficiariesKeys: (keyof BeneficiarySetupFields<
            1 | 2 | 3 | 4 | 5
          >)[] = [
            "beneficiary1",
            "beneficiary2",
            "beneficiary3",
            "beneficiary4",
            "beneficiary5",
          ];
          const filledBeneficiariesKeys = beneficiariesKeys.filter(
            (key) => formValues[key]?.name && formValues[`${key}Percentage`]
          );
          try {
            const {
              accountObjective,
              accountOpeningObjective,
              originOfFunds,
              otherOriginOfFunds,
              fundingDate,
              initialTransferAmount,
              fundingSourceAccountHolder,
              fundingSourceBankName,
              timeHorizon,
              riskProfile,
              regularWithdrawals,
              thirdPartyTransfers,
              tradeOptions,
              tradeMargins,
            } = formValues;

            const { initialValidationData, currentHolder, sendedSteps } =
              state.securities;

            if (currentHolder === 1) {
              const step: StepFiveSecurities = {
                "@type": "5_SECURITY",
                objectiveAndFunds: {
                  objective: accountObjective,
                  accountOpeningObjective,
                  originOfFunds,
                  otherFundOrigin: otherOriginOfFunds,
                  fundingPlan: Number.parseInt(fundingDate),
                  initialTransferAmount: parseFloat(initialTransferAmount),
                  fundingSourceAccountHolder,
                  fundingSourceBankName,
                  timeHorizon: timeHorizon,
                },
                riskProfile: riskProfile,
                transfersAndWithdrawals: {
                  regularWithdrawals: regularWithdrawals.bool === "SI",
                  withdrawalReason:
                    regularWithdrawals.bool === "SI"
                      ? regularWithdrawals.reason
                      : undefined,
                  otherWithdrawalReason:
                    regularWithdrawals.reason === "OTHERS"
                      ? regularWithdrawals.otherReason
                      : undefined,
                  withdrawalsFrequency:
                    regularWithdrawals.bool === "SI"
                      ? regularWithdrawals.frequency
                      : undefined,
                  otherWithdrawalsFrequency:
                    regularWithdrawals.frequency === "OTHER"
                      ? regularWithdrawals.otherFrequency
                      : undefined,
                  thirdPartyTransfers: thirdPartyTransfers.bool === "SI",
                  thirdPartyTransfersReason:
                    thirdPartyTransfers.bool === "SI"
                      ? thirdPartyTransfers.reason
                      : undefined,
                  otherThirdPartyTransfersReason:
                    thirdPartyTransfers.reason === "OTHERS"
                      ? thirdPartyTransfers.otherReason
                      : undefined,
                  transferFrequency:
                    thirdPartyTransfers.bool === "SI"
                      ? thirdPartyTransfers.frequency
                      : undefined,
                  otherTransferFrequency:
                    thirdPartyTransfers.frequency === "OTHER"
                      ? thirdPartyTransfers.otherFrequency
                      : undefined,
                },
                optionsAndMargins: {
                  interestedInOptions: tradeOptions.bool === "SI",
                  investmentObjectives:
                    tradeOptions.bool === "SI"
                      ? tradeOptions.options
                      : undefined,
                  interestedInMargins: tradeMargins.bool === "SI",
                },
                interestedInAssigningBeneficiaries: beneficiariesBool === "SI",
                beneficiaries:
                  beneficiariesBool === "SI"
                    ? filledBeneficiariesKeys.map((key) => ({
                        name: formValues[key]?.name || "",
                        assignedPercentage: parseFloat(
                          formValues[`${key}Percentage`] || ""
                        ),
                      }))
                    : undefined,
              };

              // Previous sent steps
              const previousSteps = sendedSteps.filter(
                (step: StepEndpointSecurities) => step["@type"] !== "5_SECURITY"
              );

              const body: CreateDraftProps["body"] = {
                companyType: "ALLARIA_SECURITY",
                personType: "PHYSICAL",
                email: initialValidationData.email,
                cuit: initialValidationData.taxId,
                referrer: initialValidationData.referred,
                steps: [...previousSteps, step],
              };

              try {
                await sendStep({ stepId: 5, body });
                dispatch(addSendedStep(step));
                addSubStepBreadcrumb(5, 1, "complete");
              } catch (error) {
                const requestInfo = {
                  url: `${import.meta.env.VITE_BASE_URL}/steps/5`,
                  method: "POST",
                  body: body,
                }
                throw { error, requestInfo };
              }

              if (beneficiariesBool === "SI") {
                dispatch(
                  onUpdateAccountBeneficiaries({
                    count: filledBeneficiariesKeys.length,
                    bool: true,
                  })
                );
              } else {
                const emptyStepSix: StepSixSecurities = {
                  "@type": "6_SECURITY",
                  beneficiariesInfo: [],
                };

                const previousSteps = sendedSteps.filter(
                  (step: StepEndpointSecurities) =>
                    step["@type"] !== "6_SECURITY"
                );

                const body: CreateDraftProps["body"] = {
                  companyType: "ALLARIA_SECURITY",
                  personType: "PHYSICAL",
                  email: initialValidationData.email,
                  cuit: initialValidationData.taxId,
                  referrer: initialValidationData.referred,
                  steps: [...previousSteps, step, emptyStepSix],
                };

                try {
                  await sendStep({ stepId: 6, body });
                  dispatch(addSendedStep(emptyStepSix));
                } catch (error) {
                  const requestInfo = {
                    url: `${import.meta.env.VITE_BASE_URL}/steps/6`,
                    method: "POST",
                    body: body,
                  }
                  throw { error, requestInfo };
                }

                dispatch(
                  onUpdateAccountBeneficiaries({ count: 2, bool: false })
                );
                dispatch(onUpdateCompletedStep(5));
                dispatch(onUpdateReachedStep(5));
                dispatch(onUpdateSubStep(1));
                dispatch(onUpdateLastCompletedSubStep(1));
                return { continueToPreconfirmation: true };
              }
            }
          } catch (caught: any) {
            const error = caught?.error ?? caught;
            const requestInfo = caught?.requestInfo;
            console.error("Error sending step five:", error);
            captureSecuritiesError({
              error,
              stepId: 5,
              stepName: `${title[language]} - ${subStepNames[language].planning}`,
              currentHolder: state?.securities.currentHolder || 1,
              formValues: values,
              requestInfo,
              state,
            });
            throw new Error("SECURITIES_ERROR: Error al enviar los datos en el paso 5");
          }
        },
      },
      {
        step: 2,
        name: subStepNames[language].beneficiaries,
        titleOfStep: subStepNames[language].beneficiaries,
        fields: [...Array(BENEFICIARIES_LIMIT).keys()].flatMap(
          (index: number) => fieldsForBeneficiary(index + 1)
        ),
        onContinue: async (values: FormikValues, dispatch, state) => {
          addSubStepBreadcrumb(5, 2, "enter");
          if (!state || !dispatch)
            throw new Error("Error: state or dispatch not found");

          try {
            const {
              currentHolder,
              sendedSteps,
              initialValidationData,
              USCitizen,
            } = state.securities;

            if (currentHolder === 1) {
              const formValues =
                values as SecuritiesFormikValues["stepFiveSecondSubStep"];
              const beneficiariesKeys: (keyof BeneficiaryFields<
                1 | 2 | 3 | 4 | 5
              >)[] = [
                "beneficiary1",
                "beneficiary2",
                "beneficiary3",
                "beneficiary4",
                "beneficiary5",
              ];
              const filledBeneficiariesKeys = beneficiariesKeys.filter(
                (key) => formValues[key]?.name && formValues[`${key}Percentage`]
              );

              const beneficiariesFiles = filledBeneficiariesKeys
                .filter((key) => formValues[key])
                .map((key) => {
                  const taxNumber = USCitizen
                    ? formValues[key]?.socialSecurityNumber || ""
                    : formValues[key]?.taxIdNumber || "";

                  const documentFront = formValues?.[key]?.documentFront
                    ? serializableFileAsFile(
                        formValues[key]?.documentFront as SerializableFile
                      )
                    : null;

                  const documentBack = formValues?.[key]?.documentBack
                    ? serializableFileAsFile(
                        formValues[key]?.documentBack as SerializableFile
                      )
                    : null;

                  return { documentFront, documentBack, taxNumber };
                })
                .filter(
                  ({ documentFront, documentBack }) =>
                    !!documentFront || !!documentBack
                );

              // POST each beneficiary's DNI front and back
              const uploadResults = await Promise.allSettled(
                beneficiariesFiles.map(
                  async ({ documentFront, documentBack, taxNumber }) => {
                    const res = [];
                    if (!!documentFront && !isEmptyFile(documentFront)) {
                      try {
                        const file = await heicToJpeg(
                          serializableFileAsFile(documentFront)
                        );
                        const resFront = await sendIndividualDocument({
                          draftUUID: state.securities.draftUUID,
                          cuit: taxNumber,
                          documentType: "DNI_FRONT",
                          file,
                        });
                        saveFileToFirestore?.(file.name, "DNI_FRONT");
                        res.push(resFront);
                      } catch (error) {
                        const requestInfo = {
                          url: `${import.meta.env.VITE_BASE_URL}/individual-drafts/${state.securities.draftUUID}/owners/${taxNumber}/documents?documentType=DNI_FRONT`,
                          method: "POST",
                          documentType: "DNI_FRONT",
                          cuit: taxNumber,
                        }
                        throw { error, requestInfo };
                      }
                    }
                    if (!!documentBack && !isEmptyFile(documentBack)) {
                      try {
                        const file = await heicToJpeg(
                          serializableFileAsFile(documentBack)
                        );
                        const resBack = await sendIndividualDocument({
                          draftUUID: state.securities.draftUUID,
                          cuit: taxNumber,
                          documentType: "DNI_BACK",
                          file,
                        });
                        saveFileToFirestore?.(file.name, "DNI_BACK");
                        res.push(resBack);
                      } catch (error) {
                        const requestInfo = {
                          url: `${import.meta.env.VITE_BASE_URL}/individual-drafts/${state.securities.draftUUID}/owners/${taxNumber}/documents?documentType=DNI_BACK`,
                          method: "POST",
                          documentType: "DNI_BACK",
                          cuit: taxNumber,
                        }
                        throw { error, requestInfo };
                      }
                    }
                    return res;
                  }
                )
              );

              const failedUploads = uploadResults.filter(
                (result): result is PromiseRejectedResult =>
                  result.status === "rejected"
              );
              if (failedUploads.length > 0) {
                // The catch below reports the first failure, so report the rest here
                failedUploads.slice(1).forEach(({ reason }) =>
                  captureSecuritiesError({
                    error: reason?.error ?? reason,
                    stepId: 6,
                    stepName: `${title[language]} - ${subStepNames[language].beneficiaries}`,
                    currentHolder,
                    requestInfo: reason?.requestInfo,
                    state,
                  })
                );
                throw failedUploads[0].reason;
              }

              const step: StepSixSecurities = {
                "@type": "6_SECURITY",
                beneficiariesInfo: filledBeneficiariesKeys
                  .filter((key) => formValues[key])
                  .map((key) => ({
                    name: formValues[key]?.name || "",
                    assignedPercentage: parseFloat(
                      formValues[`${key}Percentage`] || ""
                    ),
                    gender: formValues[key]?.gender ?? "OTHER",
                    birthDate: parseDateToSend(
                      formValues[key]?.birthDate ?? ""
                    ),
                    cellPhone: withPhonePlus(
                      USCitizen
                        ? formValues[key]?.phoneUS || ""
                        : formValues[key]?.phoneAR || ""
                    ),
                    email: formValues[key]?.email ?? "",
                    taxType: USCitizen
                      ? "SSN"
                      : formValues[key]?.taxIdType || "TAX_ID",
                    taxNumber: USCitizen
                      ? formValues[key]?.socialSecurityNumber || ""
                      : formValues[key]?.taxIdNumber || "",
                    relationshipWithBeneficiary:
                      formValues[key]?.relationship ?? "",
                    domicile: {
                      address: formValues[key]?.fullAddress ?? "",
                      postalCode: formValues[key]?.zipCode ?? "",
                      city: formValues[key]?.city ?? "",
                      province: formValues[key]?.state ?? "",
                      country: formValues[key]?.country ?? "",
                      ...(formValues[key]?.floor
                        ? { floor: formValues[key]?.floor }
                        : {}),
                      ...( formValues[key]?.apartment
                        ? { apartment: formValues[key]?.apartment }
                        : {}),
                    },
                  })),
              };

              // Previous sent steps
              const previousSteps = sendedSteps.filter(
                (step: StepEndpointSecurities) => step["@type"] !== "6_SECURITY"
              );

              const body: CreateDraftProps["body"] = {
                companyType: "ALLARIA_SECURITY",
                personType: "PHYSICAL",
                email: initialValidationData.email,
                cuit: initialValidationData.taxId,
                referrer: initialValidationData.referred,
                steps: [...previousSteps, step],
              };

              try {
                await sendStep({ stepId: 6, body });
                dispatch(addSendedStep(step));
                addSubStepBreadcrumb(5, 2, "complete");
                dispatch(onResetValidationErrorCounts());
              } catch (error) {

                const requestInfo = {
                  url: `${import.meta.env.VITE_BASE_URL}/steps/6`,
                  method: "POST",
                  body: body,
                }
                throw { error, requestInfo };
              }
            }
          } catch (caught: any) {
            const error = caught?.error ?? caught;
            const requestInfo = caught?.requestInfo;
            console.error("Error sending step six:", error);
            captureSecuritiesError({
              error,
              stepId: 6,
              stepName: `${title[language]} - ${subStepNames[language].beneficiaries}`,
              currentHolder: state?.securities.currentHolder || 1,
              formValues: values,
              requestInfo,
              state,
            });
            throw new Error("SECURITIES_ERROR: Error al enviar los datos en el paso 6");
          }
        },
      },
    ],
    onContinue: async (values: FormikValues, dispatch, state) => {
      if (!state || !dispatch)
        throw new Error("Error: state or dispatch not found");
      dispatch(onSaveCurrentHolder());
      console.log({
        values,
      });
    },
  };
};

export default StepFive;
